import * as d3 from 'd3';
import { SvgManager } from '../../SvgManager';
import { chartXs, chartSm } from '../../..';
import { DoughnutChartRenderer } from './DoughnutChart.renderer';
import { DoughnutChartData } from './DoughnutChart.model';
import { DoughnutChartOptions } from './DoughnutChart.options';
import { Title, Legend } from '../../pieces';
import { setSizeClasses } from '../setSizeClasses';

export class DoughnutChart<T> {
    private _height: number;
    private _width: number;
    private _mappedData: DoughnutChartData[];
    private _renderer: DoughnutChartRenderer;
    private _options: DoughnutChartOptions;
    private _title: Title;
    private _legend: Legend;

    constructor(private _svgManager: SvgManager) {
        this._width = this._svgManager.getSize().width;
        this._height = this._svgManager.getSize().height;
        this._mappedData = [];
        const container = this._svgManager.container();
        this.init(container);
        this._svgManager.addResizeFunction((width: number, height: number) => {
            this.resize(width, height);
        });
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            if (this._title) {
                this._title.ratio(value);
            }
            if (this._renderer) {
                this._renderer.ratio(value);
            }
            return this;
        }
        return this._ratio;
    }

    update(data: DoughnutChartData[]) {
        this._mappedData = data;
        this.render(data);
    }

    options(): DoughnutChartOptions;
    options(value: DoughnutChartOptions): this;
    options(value?: DoughnutChartOptions): this | DoughnutChartOptions {
        if (arguments.length) {
            this._options = value;
            return this;
        }
        return this._options;
    }

    private render(data: DoughnutChartData[]) {
        const ratio = this.ratio() || 1;
        this._legend.ratio(ratio);
        this._legend.isVisible(!!this._options.isLegendVisible);
        const legendWidth = this._legend.isVisible() ? this._legend.width() : 0;
        this._legend.translate(this._width, this._height / 2);
        const series = this._mappedData.map(s => {
            return {
                name: s.label,
                color: () => s.color,
                identifier: () => s.id,
                legendText: () => s.label,
                isVisible: true,
                legendAttributes: () => s.attributes,
                click: s.clickCallback
            } as any;
        });
        this._legend.series(series);

        this._title.ratio(ratio);
        this._title.translate((this._width - legendWidth) / 2, (-10 - (this._options.isTitleVisible ? 0 : this._title.height())) * ratio);
        this._title.isVisible(!!this._options.isTitleVisible);
        this._title.text(this._options.title);

        const titleHeight = this._title.isVisible()
            ? this._title.height() * ratio
            : 0;
        const outerRadius = (Math.min(this._width - legendWidth, this._height - titleHeight)) / 2;
        this._renderer
            .ratio(ratio)
            .thickness(this._options.thickness)
            .outerRadius(outerRadius)
            .showLabel(this._options.isLabelsVisible)
            .render(data);
        this._svgManager.container()
            .select('g.doughnut-container')
            .attr('transform', `translate(${(this._width - legendWidth) / 2 - outerRadius}, ${titleHeight})`);
    }

    private resize(width: number, height: number) {
        this._width = width;
        this._height = height;
        const container = this._svgManager.container()
            .select('g.container');
        setSizeClasses(container, width, height, this.ratio() || 1);
        this._legend.resize(width, height);
        this.render(this._mappedData);
    }

    private init(container: d3.Selection<any>) {
        const ratio = this.ratio() || 1;
        const group = container.append('g')
            .classed('container', true);
        const doughnutContainer = group.append('g')
            .classed('doughnut-container', true);
        this._renderer = new DoughnutChartRenderer(doughnutContainer)
            .ratio(ratio);
        this._title = new Title(group);
        this._title.translate(this._width / 2, -10 * ratio);
        this._title.ratio(ratio);
        this._legend = new Legend(group);
        this._legend.ratio(ratio);
    }
}